import {
  CustomFieldValueRow,
  findSameScopeCustomFieldRow,
  normalizeCustomFieldWorkspaceId,
  resolveCustomFieldCandidates
} from 'v5/core/custom-fields/resolveCustomFieldValue';

/**
 * FormBuilder `custom_fields` entry under a write lens.
 */
export type CustomFieldFormRow<
  T extends CustomFieldValueRow = CustomFieldValueRow
> =
  /** Editable slot for the current write lens (same-scope or new scoped copy). */
  | T
  /**
   * Objection keep-alive for sibling / other-scope DB rows — omit and the
   * relation update soft-deletes them. Never send `archived: false` on stubs.
   */
  | { id: number }
  /**
   * Empty FormBuilder placeholder (stable index per definition). No workspace
   * stamp — finalize drops blanks so we do not insert empty overlays.
   */
  | { custom_field_definition_id: number };

export type CustomFieldWriteLensOptions = {
  /**
   * Definition ids assigned only on the workspace metastructure overlay.
   * When set, other definitions write global (`null`) even under a workspace URL.
   * When omitted, every definition uses `browsingWorkspaceId` (legacy).
   */
  overlayDefinitionIds?: ReadonlySet<number>;
};

/**
 * Write lens for one definition: browsing workspace only for overlay-assigned
 * fields; global (`null`) for globally assigned fields when origin-aware.
 */
export function writeWorkspaceIdForDefinition(
  definitionId: number,
  browsingWorkspaceId: number | null,
  overlayDefinitionIds?: ReadonlySet<number>
): number | null {
  if (overlayDefinitionIds == null) {
    return normalizeCustomFieldWorkspaceId(browsingWorkspaceId);
  }
  return overlayDefinitionIds.has(definitionId)
    ? normalizeCustomFieldWorkspaceId(browsingWorkspaceId)
    : null;
}

/**
 * Build FormBuilder `custom_fields` array under a write lens.
 *
 * Editable rows come first (one per definition, index-stable for FormBuilder),
 * then `{ id }` sibling stubs for other-scope / unused rows so Objection does
 * not soft-delete them. Sibling stubs never send `archived: false`.
 *
 * Empty placeholders are definition-id only — workspace stamp is applied on
 * submit via `finalizeCustomFieldsFormSubmit` once a value exists.
 *
 * When `overlayDefinitionIds` is omitted, every definition uses
 * `browsingWorkspaceId` (legacy browsing lens). When provided (resource forms
 * under a workspace URL after MMS overlay merge), only those definition ids
 * write under the browsing workspace; all others write the global row (`null`).
 */
export function buildCustomFieldsFormValues<T extends CustomFieldValueRow>(
  source: T[] | null | undefined,
  definitionIds: number[],
  browsingWorkspaceId: number | null,
  options?: CustomFieldWriteLensOptions
): CustomFieldFormRow<T>[] {
  const rows = source ?? [];
  const editable: CustomFieldFormRow<T>[] = [];
  const usedIds = new Set<number>();
  const overlayDefinitionIds = options?.overlayDefinitionIds;

  for (const definitionId of definitionIds) {
    const workspaceId = writeWorkspaceIdForDefinition(
      definitionId,
      browsingWorkspaceId,
      overlayDefinitionIds
    );
    const candidates = rows.filter(
      row => row.custom_field_definition_id === definitionId
    );
    const sameScope = findSameScopeCustomFieldRow(candidates, workspaceId);

    if (sameScope) {
      if (sameScope.id != null) usedIds.add(Number(sameScope.id));
      editable.push({
        ...sameScope,
        enterprise_workspace_id: workspaceId,
        archived: false
      } as T);
      continue;
    }

    // Workspace write lens with no same-scope row: may prefill from *global*
    // only (org default). Never copy another workspace's values.
    const fallback = resolveCustomFieldCandidates(candidates, workspaceId);
    if (
      workspaceId != null &&
      fallback != null &&
      normalizeCustomFieldWorkspaceId(fallback.enterprise_workspace_id) === null
    ) {
      const { id: _omitId, ...rest } = fallback;
      editable.push({
        ...rest,
        custom_field_definition_id: definitionId,
        enterprise_workspace_id: workspaceId,
        archived: false
      } as T);
      continue;
    }

    // Empty form placeholder — no workspace stamp (avoid blank overlay inserts).
    editable.push({
      custom_field_definition_id: definitionId
    });
  }

  const siblingStubs: { id: number }[] = [];
  for (const row of rows) {
    if (row.id == null) continue;
    const id = Number(row.id);
    if (usedIds.has(id)) continue;
    siblingStubs.push({ id });
  }

  return [...editable, ...siblingStubs];
}

/**
 * FormBuilder submit: drop empty new placeholders; stamp write lens on
 * real writes; leave `{ id }` sibling stubs untouched.
 */
export function finalizeCustomFieldsFormSubmit<T extends CustomFieldValueRow>(
  customFields: T[] | null | undefined,
  browsingWorkspaceId: number | null,
  existingRows?: CustomFieldValueRow[] | null,
  options?: CustomFieldWriteLensOptions
): Array<T | { id: number }> {
  if (!Array.isArray(customFields)) return [];
  const overlayDefinitionIds = options?.overlayDefinitionIds;

  return customFields.flatMap((field): Array<T | { id: number }> => {
    const keys = Object.keys(field);
    if (keys.length === 1 && field.id != null) {
      return [{ id: Number(field.id) }];
    }
    if (field.id == null && !customFieldHasWriteValue(field)) {
      return [];
    }

    const definitionId = field.custom_field_definition_id;
    const lens =
      definitionId != null
        ? writeWorkspaceIdForDefinition(
            definitionId,
            browsingWorkspaceId,
            overlayDefinitionIds
          )
        : normalizeCustomFieldWorkspaceId(browsingWorkspaceId);

    // Reattach same-scope id when the form lost it (react-form first keystroke)
    // so Objection updates instead of inserting a duplicate unique slot.
    let id = field.id != null ? Number(field.id) : null;
    if (id == null && definitionId != null && Array.isArray(existingRows)) {
      const same = findSameScopeCustomFieldRow(
        existingRows.filter(
          row => row.custom_field_definition_id === definitionId
        ),
        lens
      );
      if (same?.id != null) {
        id = Number(same.id);
      }
    }

    return [
      {
        ...field,
        ...(id != null ? { id } : {}),
        enterprise_workspace_id: lens
      } as T
    ];
  });
}

export function finalizeEntityCustomFieldsFormSubmit<
  T extends {
    custom_fields?: CustomFieldValueRow[] | null;
    custom_fieldgroups?: Array<{
      custom_fields?: CustomFieldValueRow[] | null;
      [key: string]: unknown;
    }> | null;
    [key: string]: unknown;
  }
>(
  data: T,
  browsingWorkspaceId: number | null,
  existingEntity?: {
    custom_fields?: CustomFieldValueRow[] | null;
    custom_fieldgroups?: Array<{
      custom_fields?: CustomFieldValueRow[] | null;
    }> | null;
  } | null,
  options?: CustomFieldWriteLensOptions
): T {
  return {
    ...data,
    custom_fields: finalizeCustomFieldsFormSubmit(
      data.custom_fields,
      browsingWorkspaceId,
      existingEntity?.custom_fields,
      options
    ),
    custom_fieldgroups: Array.isArray(data.custom_fieldgroups)
      ? data.custom_fieldgroups.map((group, index) => ({
          ...group,
          custom_fields: finalizeCustomFieldsFormSubmit(
            group.custom_fields,
            browsingWorkspaceId,
            existingEntity?.custom_fieldgroups?.[index]?.custom_fields,
            options
          )
        }))
      : data.custom_fieldgroups
  };
}

function customFieldHasWriteValue(field: CustomFieldValueRow): boolean {
  if (field.boolean_value === true || field.boolean_value === false)
    return true;
  if (field.option_id != null) return true;
  if (
    Array.isArray(field.multi_select_ids) &&
    field.multi_select_ids.length > 0
  ) {
    return true;
  }
  if (field.string_value != null && field.string_value !== '') return true;
  if (field.integer_value != null) return true;
  if (field.float_value != null) return true;
  if (field.date_value != null) return true;
  if (field.raw_value != null && field.raw_value !== '') return true;
  return false;
}
